import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import { PlannedAction } from 'types/iapBaseTypes';

import { ICS204_TABLE } from '../../AssignmentList/assignmentCardStyles';
import PlannedActionEditDrawer from './PlannedActionEditDrawer';

type Row = { id: string; value: PlannedAction };

const BLANK_ACTION: PlannedAction = {
  task: '',
  pointOfContact: '',
  pocBriefed: false,
  startDate: null,
  deadline: null,
  status: null,
};

const formatDate = (value: Date | null): string =>
  value ? new Date(value).toLocaleDateString() : '';

/**
 * Box 14, the actions planned for the next operational period.
 *
 * Unlike the 209's other lists the row is not edited in place: an action
 * carries six fields, so the pencil opens a dialog. A new action is only
 * appended once that dialog saves, so dismissing it leaves nothing behind.
 */
const PlannedActionsTable = ({
  statusSummaryId,
  originalPlannedActions = [],
  readOnly = false,
}: {
  statusSummaryId: string;
  originalPlannedActions?: PlannedAction[];
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const nextId = useRef(0);
  const makeRows = useCallback(
    (items: PlannedAction[]): Row[] =>
      items.map((value) => ({ id: `row-${nextId.current++}`, value })),
    []
  );

  const [rows, setRows] = useState<Row[]>(() =>
    makeRows(originalPlannedActions)
  );
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  /** The row the dialog is editing, or 'new' while adding. */
  const [editing, setEditing] = useState<Row | 'new' | null>(null);

  // Take server state only while the dialog is closed, so a refetch cannot
  // overwrite an action someone is part way through filling in.
  const serialized = JSON.stringify(originalPlannedActions);
  useEffect(() => {
    if (editing !== null) return;
    setRows((current) => {
      if (JSON.stringify(current.map((row) => row.value)) === serialized) {
        return current;
      }
      setSelectedIds([]);
      return makeRows(originalPlannedActions);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, editing]);

  const commitList = (nextRows: Row[]) => {
    setRows(nextRows);
    upsertIncidentStatusSummary({
      plannedActions: nextRows.map((row) => row.value),
    });
  };

  const visibleRows = useMemo(() => {
    const query = search.toLowerCase();
    return rows.filter((row) => row.value.task.toLowerCase().includes(query));
  }, [rows, search]);

  const allSelected =
    visibleRows.length > 0 && selectedIds.length === visibleRows.length;

  const savePlannedAction = (action: PlannedAction) => {
    if (editing === 'new') {
      commitList([...rows, { id: `row-${nextId.current++}`, value: action }]);
    } else if (editing) {
      commitList(
        rows.map((row) =>
          row.id === editing.id ? { ...row, value: action } : row
        )
      );
    }
    setEditing(null);
  };

  const deleteRows = (ids: string[]) => {
    commitList(rows.filter((row) => !ids.includes(row.id)));
    setSelectedIds([]);
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label="Search planned actions"
          placeholder="Search planned actions..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            className="tw:ml-auto"
            onClick={() => setEditing('new')}
          >
            <Plus className="tw:size-4" />
            Add Planned Action
          </Button>
        )}
      </div>

      {!readOnly && selectedIds.length > 0 && (
        <div className="tw:flex tw:items-center tw:justify-between">
          <span className="tw:text-sm">{selectedIds.length} selected</span>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => deleteRows(selectedIds)}
          >
            <Trash2 className="tw:size-4" />
            Delete Selected
          </Button>
        </div>
      )}

      <Table className={ICS204_TABLE} data-testid="ics209-planned-actions">
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-10">
              <Checkbox
                aria-label="Select all planned actions"
                checked={allSelected}
                disabled={visibleRows.length === 0}
                onCheckedChange={() =>
                  setSelectedIds(
                    allSelected ? [] : visibleRows.map((row) => row.id)
                  )
                }
              />
            </TableHead>
            <TableHead>Task</TableHead>
            <TableHead>Point of Contact</TableHead>
            <TableHead>POC Briefed</TableHead>
            <TableHead>Start Date</TableHead>
            <TableHead>Deadline</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="tw:w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="tw:text-center">
                {search
                  ? 'No results!'
                  : 'There are no planned actions yet. Click Add Planned Action to add one.'}
              </TableCell>
            </TableRow>
          ) : (
            visibleRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Checkbox
                    aria-label={`Select ${row.value.task}`}
                    checked={selectedIds.includes(row.id)}
                    onCheckedChange={() =>
                      setSelectedIds((current) =>
                        current.includes(row.id)
                          ? current.filter((id) => id !== row.id)
                          : [...current, row.id]
                      )
                    }
                  />
                </TableCell>
                <TableCell>{row.value.task}</TableCell>
                <TableCell>{row.value.pointOfContact ?? ''}</TableCell>
                <TableCell>
                  <Checkbox
                    aria-label={`POC briefed for ${row.value.task}`}
                    checked={row.value.pocBriefed}
                    disabled
                  />
                </TableCell>
                <TableCell>{formatDate(row.value.startDate)}</TableCell>
                <TableCell>{formatDate(row.value.deadline)}</TableCell>
                <TableCell>{row.value.status ?? ''}</TableCell>
                <TableCell>
                  {readOnly ? null : (
                    <div className="tw:flex tw:justify-end tw:gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${row.value.task}`}
                        onClick={() => setEditing(row)}
                      >
                        <Pencil className="tw:size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${row.value.task}`}
                        onClick={() => deleteRows([row.id])}
                      >
                        <Trash2 className="tw:size-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <span className="tw:text-sm tw:text-muted-foreground">
        {visibleRows.length} planned actions
      </span>

      {!readOnly && editing ? (
        <PlannedActionEditDrawer
          isNewAction={editing === 'new'}
          selectedAction={editing === 'new' ? BLANK_ACTION : editing.value}
          onDismiss={() => setEditing(null)}
          onSave={savePlannedAction}
        />
      ) : null}
    </div>
  );
};

export default PlannedActionsTable;
